import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Container,
  Button,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TablePagination,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { cryopreservationService } from '../services/cryopreservationService';
import CryopreservationModal from '../components/CryopreservationModal';
import RemainingSpaceModal from '../components/RemainingSpaceModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastProvider';
import { glassCardSx, glassDialogPaperProps } from '../styles/tokens';
import { formatDisplayDate } from '../utils/dateUtils';
import PageHeader from '../components/ui/PageHeader';

// Column config drives the table head, rows, the show/hide menu, and the mobile
// card view — one source of truth so all four stay in sync.
type ColumnId =
  | 'cell_line_name' | 'origin' | 'passage_number' | 'number_of_vials_frozen'
  | 'box_number' | 'position_in_box' | 'frozen_by' | 'date_of_cryo';

interface ColumnDef {
  id: ColumnId;
  label: string;
  minWidth: number;
  always?: boolean; // cannot be hidden
  render?: (item: any) => React.ReactNode;
}

const COLUMNS: ColumnDef[] = [
  { id: 'cell_line_name', label: 'Cell Line Name', minWidth: 200, always: true },
  { id: 'origin', label: 'Origin', minWidth: 110 },
  { id: 'passage_number', label: 'Passage', minWidth: 100 },
  { id: 'number_of_vials_frozen', label: 'Vials Frozen', minWidth: 110 },
  { id: 'box_number', label: 'Box Number', minWidth: 110 },
  { id: 'position_in_box', label: 'Position', minWidth: 110 },
  { id: 'frozen_by', label: 'Frozen By', minWidth: 130 },
  { id: 'date_of_cryo', label: 'Date of Cryo', minWidth: 130, render: (i) => (i.date_of_cryo ? formatDisplayDate(i.date_of_cryo) : '-') },
];

const HIDDEN_COLS_KEY = 'cryo.hiddenColumns';
const DENSITY_KEY = 'cryo.density';

// CryopreservationModal is still a JS component; treat as accepting any props
// until it is migrated to TypeScript.
const CryoModal = CryopreservationModal as unknown as React.ComponentType<any>;

// Compact labelled dropdown used in the Records filter bar.
function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 170, flex: '1 1 170px', maxWidth: 240 }}
    >
      <MenuItem value="">
        <em>All</em>
      </MenuItem>
      {options.map((opt, idx) => (
        <MenuItem key={opt.value + '-' + idx} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

const Cryopreservation = () => {
  const { user } = useAuth();
  const userRole = user?.role || 'viewer';
  const queryClient = useQueryClient();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width:768px)');

  // View state is mirrored to the URL so a reload / shared link restores the
  // same search, filters, sort and page.
  const [searchParams, setSearchParams] = useSearchParams();
  const initParam = (k: string, d = '') => searchParams.get(k) ?? d;

  const [searchTerm, setSearchTerm] = useState(initParam('q'));
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBatchUploadModal, setShowBatchUploadModal] = useState(false);
  const [showRemainingSpaceModal, setShowRemainingSpaceModal] = useState(false);
  const [remainingSpaceData, setRemainingSpaceData] = useState([]);
  const [remainingSpaceLoading, setRemainingSpaceLoading] = useState(false);
  const [remainingSpaceError, setRemainingSpaceError] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    cell_line_name: '',
    origin: '',
    passage_number: '',
    frozen_by: '',
    number_of_cells_frozen: '',
    number_of_vials_frozen: '',
    storage_location: 'Liquid Nitrogen',
    box_number: '',
    position_in_box: '',
    date_of_cryo: '',
    time_of_freezing: '',
    notes: ''
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  const [esignPassword, setEsignPassword] = useState("");
  const [esignReason, setEsignReason] = useState("");
  const [esignError, setEsignError] = useState("");
  const [deleteRecordId, setDeleteRecordId] = useState(null);
  const [approveMode, setApproveMode] = useState(false);
  const [orderBy, setOrderBy] = useState(initParam('orderBy', 'cell_line_name'));
  const [order, setOrder] = useState<'asc' | 'desc'>((initParam('order', 'asc') as 'asc' | 'desc'));
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(Number(initParam('page', '0')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(initParam('limit', '25')) || 25);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectTargetName, setRejectTargetName] = useState('');

  // Row selection (bulk actions)
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Row action menu (per-row kebab)
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowMenuItem, setRowMenuItem] = useState<any>(null);
  // Column show/hide + density, persisted to localStorage.
  const [colMenuAnchor, setColMenuAnchor] = useState<null | HTMLElement>(null);
  const [hiddenCols, setHiddenCols] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) || '[]'); } catch { return []; }
  });
  const [density, setDensity] = useState<'comfortable' | 'compact'>(
    () => (localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable'),
  );

  // Filter state
  const [filterValues, setFilterValues] = useState({
    origin: initParam('origin'),
    frozenBy: initParam('frozenBy'),
    boxNumber: initParam('boxNumber'),
    positionInBox: initParam('positionInBox'),
  });

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => c.always || !hiddenCols.includes(c.id)),
    [hiddenCols],
  );
  const bulkEnabled = userRole !== 'viewer';
  const [filterOptions, setFilterOptions] = useState({
    origins: [],
    frozenBy: [],
    boxNumbers: [],
    positionsInBox: []
  });

  const isReviewer = userRole === 'admin' || userRole === 'approver';

  // Debounce the search term so typing doesn't fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Mirror view state into the URL (shareable / reload-safe). Only non-default
  // values are written so the URL stays clean.
  useEffect(() => {
    const next: Record<string, string> = {};
    if (debouncedSearch) next.q = debouncedSearch;
    if (filterValues.origin) next.origin = filterValues.origin;
    if (filterValues.frozenBy) next.frozenBy = filterValues.frozenBy;
    if (filterValues.boxNumber) next.boxNumber = filterValues.boxNumber;
    if (filterValues.positionInBox) next.positionInBox = filterValues.positionInBox;
    if (orderBy !== 'cell_line_name') next.orderBy = orderBy;
    if (order !== 'asc') next.order = order;
    if (page !== 0) next.page = String(page);
    if (rowsPerPage !== 25) next.limit = String(rowsPerPage);
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, filterValues, orderBy, order, page, rowsPerPage, setSearchParams]);

  // React to the `q` param changing externally (e.g. a notification deep-link
  // while this page is already mounted).
  useEffect(() => {
    const urlQ = searchParams.get('q') ?? '';
    if (urlQ !== searchTerm) {
      setSearchTerm(urlQ);
      setDebouncedSearch(urlQ);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Persist column + density preferences.
  useEffect(() => {
    localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(hiddenCols));
  }, [hiddenCols]);
  useEffect(() => {
    localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  // Main list query (TanStack Query). Keys on every param that affects results,
  // so it refetches automatically when they change and caches per-combination.
  const listQuery = useQuery({
    queryKey: [
      'cryo-list', page, rowsPerPage, debouncedSearch,
      filterValues.origin, filterValues.frozenBy, filterValues.boxNumber, filterValues.positionInBox,
      orderBy, order,
    ],
    queryFn: () =>
      debouncedSearch
        ? cryopreservationService.searchCryopreservations(
            debouncedSearch, page + 1, rowsPerPage,
            filterValues.origin, filterValues.frozenBy, filterValues.boxNumber, filterValues.positionInBox,
            '', orderBy, order,
          )
        : cryopreservationService.getAllCryopreservations(
            page + 1, rowsPerPage, '',
            filterValues.origin, filterValues.frozenBy, filterValues.boxNumber, filterValues.positionInBox,
            '', orderBy, order,
          ),
    placeholderData: (prev) => prev,
  });

  const cryopreservations = listQuery.data?.items ?? [];
  const totalItems = listQuery.data?.total ?? 0;
  // Skeletons only on the very first load (no cached data yet). Background
  // refetches — the 30s-independent invalidations, filter/sort/page changes —
  // keep the previous rows on screen (placeholderData) and show a thin top
  // progress bar instead, so the table never flickers to skeletons.
  const loading = listQuery.isLoading;
  const isRefetching = listQuery.isFetching && !listQuery.isLoading;

  const rowId = (item: any) => String(item._id ?? item.id);
  // Drop selections that are no longer on the current page/result set.
  useEffect(() => {
    const ids = new Set(cryopreservations.map(rowId));
    setSelectedIds((prev) => prev.filter((id) => ids.has(id)));
  }, [cryopreservations]);

  // Pending-deletion approvals queue (admins & approvers only).
  const pendingQuery = useQuery({
    queryKey: ['pending-deletions'],
    queryFn: () => cryopreservationService.getPendingDeletions(),
    enabled: isReviewer,
  });
  const pendingDeletions = pendingQuery.data ?? [];

  // Surface list-load failures in the existing error banner.
  useEffect(() => {
    if (listQuery.error) {
      setError((listQuery.error as Error).message || 'Failed to load cryopreservations');
    }
  }, [listQuery.error]);

  // Refresh helper used by mutation handlers (invalidate → refetch).
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['cryo-list'] });
    queryClient.invalidateQueries({ queryKey: ['pending-deletions'] });
  };

  // Load filter options
  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await cryopreservationService.getFilterOptions();
      // Deduplicate all filter arrays before mapping
      const unique = arr => Array.from(new Set(arr.filter(x => x !== undefined && x !== null)));
      setFilterOptions({
        origins: unique(options.origins).map(value => ({ value, label: value })),
        frozenBy: unique(options.frozenBy).map(value => ({ value, label: value })),
        boxNumbers: unique(options.boxNumbers).map(value => ({ value: String(value), label: String(value) })),
        positionsInBox: unique(options.positionsInBox || []).map(value => ({ value: String(value), label: String(value) }))
      });
    } catch (err) {
      // Filter options are non-critical (the page still works without them),
      // so don't surface a blocking error — but do log it for diagnosis.
      console.error('Failed to load filter options', err);
    }
  }, []);

  // (Data loading is handled by the TanStack Query hook above.)

  // Effect to load filter options (runs independently)
  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);
  
  const handleFilterChange = (filterId, value) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: value
    }));
    setPage(0); // Reset page on filter change
  };

  const clearAllFilters = () => {
    setFilterValues({ origin: '', frozenBy: '', boxNumber: '', positionInBox: '' });
    setPage(0);
  };

  // Filter params shared by the list query and the export endpoint.
  const currentFilterParams = () => ({
    search_term: debouncedSearch,
    origin: filterValues.origin,
    frozen_by: filterValues.frozenBy,
    boxNumber: filterValues.boxNumber,
    positionInBox: filterValues.positionInBox,
  });

  // Export honours the active view; when rows are selected, only those.
  const handleExport = async () => {
    try {
      setExporting(true);
      const filters: Record<string, any> = currentFilterParams();
      if (selectedIds.length > 0) filters.ids = selectedIds.join(',');
      await cryopreservationService.exportToExcel(filters);
      toast.success(
        selectedIds.length > 0
          ? `Exported ${selectedIds.length} selected record(s).`
          : 'Exported the current view to Excel.',
      );
    } catch (err: any) {
      toast.error(err.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  // --- Row selection ---
  const allOnPageSelected =
    cryopreservations.length > 0 && cryopreservations.every((i) => selectedIds.includes(rowId(i)));
  const someOnPageSelected =
    cryopreservations.some((i) => selectedIds.includes(rowId(i))) && !allOnPageSelected;
  const toggleSelectAll = () => {
    const ids = cryopreservations.map(rowId);
    setSelectedIds((prev) => (allOnPageSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))));
  };
  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // --- Row action menu ---
  const openRowMenu = (e: React.MouseEvent<HTMLElement>, item: any) => {
    e.stopPropagation();
    setRowMenuItem(item);
    setRowMenuAnchor(e.currentTarget);
  };
  const closeRowMenu = () => { setRowMenuAnchor(null); setRowMenuItem(null); };

  // --- Column show/hide ---
  const toggleColumn = (id: string) =>
    setHiddenCols((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  // --- Bulk delete request (each still routed through the e-signature flow) ---
  const openBulkDelete = () => {
    setBulkDeleteMode(true);
    setApproveMode(false);
    setDeleteRecordId(null);
    setEsignPassword('');
    setEsignReason('');
    setEsignError('');
    setEsignOpen(true);
  };

  const handleAddItem = async (formData) => {
    try {
      setModalLoading(true);
      setError("");
      await cryopreservationService.addCryopreservation(formData);
      toast.success('Cryopreservation record added.');
    setShowAddModal(false);
      setFormData({
        cell_line_name: '',
        origin: '',
        passage_number: '',
        frozen_by: '',
        number_of_cells_frozen: '',
        number_of_vials_frozen: '',
        storage_location: 'Liquid Nitrogen',
        box_number: '',
        position_in_box: '',
        date_of_cryo: '',
        time_of_freezing: '',
        notes: ''
      });
      refresh();
    } catch (err) {
      const errorMessage = err.message || "Failed to add item";
      setError(errorMessage);
      setShowAddModal(true);
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditItem = async (formData) => {
    try {
      setModalLoading(true);
      setError("");
      
      const formattedData = {
        ...formData,
        notes: formData.notes || "",
        revival_history: formData.revival_history || [],
        box_number: formData.box_number ? String(formData.box_number) : "",
        position_in_box: formData.position_in_box ? String(formData.position_in_box) : "",
        date_of_cryo: formData.date_of_cryo || null,
        time_of_freezing: formData.time_of_freezing || null,
        frozen_by: formData.frozen_by || "",
        number_of_cells_frozen: formData.number_of_cells_frozen || null,
        passage_number: formData.passage_number || "",
        origin: formData.origin || ""
      };

      await cryopreservationService.updateCryopreservation(currentItem._id, formattedData);
      toast.success('Changes saved.');
      setShowEditModal(false);
      
      // Always reload data after successful update
      refresh(); 

    } catch (err) {
      setError(err.message || "Failed to update item");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteItem = async (id) => {
    setDeleteRecordId(id);
    setEsignPassword("");
    setEsignReason("");
    setEsignError("");
    setApproveMode(false);
    setEsignOpen(true);
  };

  const handleApproveDeleteItem = (id) => {
    const pendingItem = pendingDeletions.find(item => item._id === id || item.id === id);
    setDeleteRecordId(id);
    setEsignPassword("");
    setEsignReason(pendingItem?.deletion_reason || "");
    setEsignError("");
    setApproveMode(true);
    setEsignOpen(true);
  };

  const handleRejectDeleteItem = (id) => {
    const pendingItem = pendingDeletions.find(item => item._id === id || item.id === id);
    setRejectTargetId(id);
    setRejectTargetName(pendingItem?.cell_line_name || 'this record');
    setRejectConfirmOpen(true);
  };

  const handleConfirmReject = async () => {
    setRejectConfirmOpen(false);
    try {
      setModalLoading(true);
      setError("");
      await cryopreservationService.rejectDeletion(rejectTargetId);
      toast.success("Deletion request rejected.");
      refresh();
    } catch (err) {
      setError(err.message || "Failed to reject deletion request.");
    } finally {
      setModalLoading(false);
      setRejectTargetId(null);
      setRejectTargetName('');
    }
  };

  const handleConfirmDeleteEsign = async () => {
    if (!esignPassword) {
      setEsignError("Your password is required to electronically sign.");
      return;
    }
    if (!approveMode && !esignReason.trim()) {
      setEsignError("A reason for deletion is required.");
      return;
    }
    
    try {
      setModalLoading(true);
      setEsignError("");
      setError("");
      
      if (bulkDeleteMode) {
        // Bulk: submit a signed deletion (or deletion request) per selected row.
        const ids = [...selectedIds];
        const results = await Promise.allSettled(
          ids.map((id) =>
            cryopreservationService.deleteCryopreservation(id, esignReason.trim(), esignPassword),
          ),
        );
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const failed = results.length - ok;
        if (failed) toast.warning(`${ok} record(s) processed, ${failed} failed.`);
        else toast.success(`${ok} record(s) processed.`);
        setSelectedIds([]);
        setBulkDeleteMode(false);
      } else if (approveMode) {
        // Approve Deletion Workflow
        const response = await cryopreservationService.approveDeletion(deleteRecordId, esignPassword);
        toast.success(response.message || "Deletion approved and record soft-deleted.");
      } else {
        // Request Deletion or Direct Deletion Workflow
        const response = await cryopreservationService.deleteCryopreservation(deleteRecordId, esignReason.trim(), esignPassword);
        if (response && response.status === "pending_approval") {
          toast.info(response.message || "Deletion request submitted for approval by your mapped approver.");
        } else {
          toast.success("Record deleted.");
        }
      }
      
      setEsignOpen(false);
      setShowDetailModal(false);
      refresh();
    } catch (err) {
      setEsignError(err.message || "Electronic signature verification failed.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleReviveItem = async (id, revivalDetails) => {
    try {
      setModalLoading(true);
      
      // Ensure we're passing valid data. Revival can be driven by picked
      // positions or a plain vial count.
      const hasPositions = revivalDetails?.positions_to_revive &&
        (Array.isArray(revivalDetails.positions_to_revive)
          ? revivalDetails.positions_to_revive.length > 0
          : !!revivalDetails.positions_to_revive);
      if (!id || !revivalDetails || (!hasPositions && !revivalDetails.vials_to_revive) || !revivalDetails.revived_by) {
        throw new Error("Missing required revival information");
      }
      
      const result = await cryopreservationService.reviveCryopreservation(id, revivalDetails);
      setShowDetailModal(false);
      
      // Reload data after successful revive
      refresh();
      
      return result; // Return the result for the modal to use
    } catch (err) {
      setError(err.message || "Failed to revive item");
      throw err; // Re-throw to allow the modal to catch and display the error
    } finally {
      setModalLoading(false);
    }
  };

  const openEditModal = (item) => {
    setCurrentItem(item);
    setFormData({ 
      ...formData,
      ...item
    });
    setShowEditModal(true);
    setShowDetailModal(false);
    setError("");
  };

  const openDetailModal = (item) => {
    setCurrentItem(item);
    setShowDetailModal(true);
    setError("");
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    setPage(0); // Reset page on sort change
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const handleSearch = () => {
    setPage(0); // Reset pagination when a new search is performed
    setDebouncedSearch(searchTerm); // apply immediately on Enter (bypass debounce)
  };

  const handleShowRemainingSpace = async () => {
    setShowRemainingSpaceModal(true);
    setRemainingSpaceLoading(true);
    setRemainingSpaceError("");
    try {
      // Fetch box space and inventory stats together; stats are supplementary,
      // so a stats failure must not block the box-space table from showing.
      const [data, stats] = await Promise.all([
        cryopreservationService.getRemainingBoxSpace(),
        cryopreservationService.getStatistics().catch((statsErr) => {
          console.error('Failed to load statistics', statsErr);
          return null;
        }),
      ]);
      setRemainingSpaceData(data);
      setStatsData(stats);
    } catch (err) {
      setRemainingSpaceError(err.message || 'Failed to load remaining box space');
    } finally {
      setRemainingSpaceLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 0, width: '100%', boxSizing: 'border-box' }}>
      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center">
            <Box mr={1}><i aria-hidden className="fas fa-exclamation-triangle"></i></Box>
            <Box>
              <strong>Error:</strong> {error}
              <Box mt={1}>
                <Button variant="outlined" color="secondary" size="small" onClick={() => listQuery.refetch()} sx={{ borderRadius: 'var(--radius-xs)' }}>Try Again</Button>
              </Box>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Pending Deletion Approvals Queue (Admin & Approver Only) — surfaced at
          the very top of the page so reviewers see action-required items first,
          rather than buried below the search and filters. */}
      {(userRole === 'admin' || userRole === 'approver') && pendingDeletions.length > 0 && (
        <Paper
          className="glassmorphism"
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 'var(--radius-md)',
            border: '1px solid color-mix(in oklab, var(--color-error) 25%, transparent)',
            background: 'color-mix(in oklab, var(--color-error) 4%, var(--color-surface))',
            boxShadow: 'var(--shadow-e2)',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={1.5} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1.5}>
              <i aria-hidden className="fas fa-exclamation-circle" style={{ color: 'var(--color-error)', fontSize: '1.25rem' }}></i>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'var(--color-error)', fontSize: '1.1rem' }}>
                Pending Deletion Approvals Queue
              </Typography>
              <Chip
                label={`${pendingDeletions.length} Action Required`}
                color="error"
                size="small"
                sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: 20 }}
              />
            </Box>
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'var(--color-muted)' }}>
              21 CFR Part 11 Compliance Module
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-outline)',
              maxHeight: '300px',
              bgcolor: 'var(--color-surface)'
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Cell Line Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Requested By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Assigned Approver</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>Reason for Deletion</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingDeletions.map((item) => (
                  <TableRow key={item._id || item.id} hover>
                    <TableCell sx={{ fontWeight: 'medium' }}>{item.cell_line_name}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{item.deletion_requested_by}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{item.deletion_approver}</TableCell>
                    <TableCell sx={{ fontStyle: 'italic', color: 'var(--color-muted)', fontSize: '0.8rem' }}>
                      "{item.deletion_reason || 'No reason provided'}"
                    </TableCell>
                    <TableCell sx={{ py: 0.5, textAlign: 'center' }}>
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<i aria-hidden className="fas fa-key" style={{ fontSize: 11 }}></i>}
                          onClick={() => handleApproveDeleteItem(item._id || item.id)}
                          sx={{ borderRadius: 'var(--radius-xs)', textTransform: 'none', py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 'bold' }}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRejectDeleteItem(item._id || item.id)}
                          sx={{ borderRadius: 'var(--radius-xs)', textTransform: 'none', py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 'bold' }}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <PageHeader
        title="Records"
        subtitle="Browse, search and manage cell-line cryopreservation records."
        actions={
          <>
            {userRole !== 'viewer' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowAddModal(true)}
                sx={{ fontWeight: 600, borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-e1)' }}
                startIcon={<i aria-hidden className="fas fa-plus"></i>}
              >
                Add New Record
              </Button>
            )}
            {/* Utility actions — grouped and visually secondary to the primary
                create action above. */}
            <Button
              variant="outlined"
              color="primary"
              onClick={handleShowRemainingSpace}
              sx={{ fontWeight: 600, borderRadius: 'var(--radius-sm)', borderWidth: 1.5 }}
              startIcon={<i aria-hidden className="fas fa-boxes"></i>}
            >
              Remaining Box Space
            </Button>
            {userRole !== 'viewer' && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExport}
                disabled={loading || exporting}
                sx={{ fontWeight: 600, borderRadius: 'var(--radius-sm)', borderWidth: 1.5 }}
                startIcon={exporting ? <CircularProgress size={16} /> : <i aria-hidden className="fas fa-file-excel"></i>}
              >
                {selectedIds.length > 0 ? `Export ${selectedIds.length} Selected` : 'Export to Excel'}
              </Button>
            )}
            {userRole === 'admin' && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowBatchUploadModal(true)}
                sx={{ fontWeight: 600, borderRadius: 'var(--radius-sm)', borderWidth: 1.5 }}
                startIcon={<i aria-hidden className="fas fa-file-upload"></i>}
              >
                Batch Upload
              </Button>
            )}
          </>
        }
      />

      <Card sx={{
        mb: 4,
        ...glassCardSx,
        transition: 'background 0.3s, box-shadow 0.3s',
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Thin top progress bar during background refetches — keeps the
            existing rows visible instead of flashing skeletons. */}
        {isRefetching && (
          <LinearProgress
            aria-label="Refreshing records"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              zIndex: 2,
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': { backgroundColor: 'var(--color-primary)' },
            }}
          />
        )}
        <CardHeader
          sx={{
            bgcolor: 'transparent',
            color: 'var(--color-fg)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            '& .MuiCardHeader-title': {
              fontSize: '1.1rem',
              fontWeight: 600
            }
          }}
          title={
            <Box display="flex" alignItems="center" gap={1.25}>
              <i className="fas fa-list" style={{ color: 'var(--color-primary)' }} />
              <span>Records</span>
              <Chip
                label={`${totalItems} items`}
                size="small"
                sx={{
                  bgcolor: 'var(--color-primary-container)',
                  color: 'var(--color-on-primary-container)',
                  fontWeight: 700,
                  height: 22,
                }}
              />
            </Box>
          }
        />
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid var(--color-outline)' }}>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Search cell line, origin, frozen by, or box…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0); // Reset page on search term change
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'var(--color-surface-3)',
                    '& fieldset': { borderColor: 'var(--color-outline)' },
                    '&:hover fieldset': { borderColor: 'var(--color-outline-strong)' },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--color-primary)',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'var(--color-fg)',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'var(--color-muted)',
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i aria-hidden className="fas fa-search" style={{
                        fontSize: '1.1rem',
                        color: 'var(--color-muted)'
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                        aria-label="Clear search"
                        sx={{
                          color: 'var(--color-muted)',
                          '&:hover': {
                            color: 'var(--color-fg)',
                            bgcolor: 'var(--color-surface-2)'
                          }
                        }}
                      >
                        <i aria-hidden className="fas fa-times" style={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                aria-pressed={showFilters}
                sx={{
                  borderRadius: 'var(--radius-xs)',
                  color: showFilters ? 'var(--color-primary)' : 'var(--color-muted)',
                  bgcolor: showFilters ? 'color-mix(in oklab, var(--color-primary) 10%, transparent)' : 'transparent',
                  transition: 'color 0.2s, background 0.2s',
                  '&:hover': { bgcolor: 'var(--color-surface-2)' },
                }}
              >
                <i aria-hidden className={`fas ${showFilters ? 'fa-filter-circle-xmark' : 'fa-filter'}`} style={{ fontSize: '1.2rem' }}></i>
              </IconButton>
            </Box>

            {/* Active Filters */}
            {Object.values(filterValues).some((value) => value) && (
              <Box sx={{ display: 'flex', mt: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'var(--color-muted)', fontWeight: 700, mr: 0.5 }}>
                  Active
                </Typography>
                {filterValues.origin && (
                  <Chip label={`Origin: ${filterValues.origin}`} color="primary" size="small"
                    onDelete={() => handleFilterChange('origin', '')} sx={{ borderRadius: 'var(--radius-xs)' }} />
                )}
                {filterValues.frozenBy && (
                  <Chip label={`Frozen By: ${filterValues.frozenBy}`} color="secondary" size="small"
                    onDelete={() => handleFilterChange('frozenBy', '')} sx={{ borderRadius: 'var(--radius-xs)' }} />
                )}
                {filterValues.boxNumber && (
                  <Chip label={`Box: ${filterValues.boxNumber}`} color="success" size="small"
                    onDelete={() => handleFilterChange('boxNumber', '')} sx={{ borderRadius: 'var(--radius-xs)' }} />
                )}
                {filterValues.positionInBox && (
                  <Chip label={`Position: ${filterValues.positionInBox}`} size="small"
                    onDelete={() => handleFilterChange('positionInBox', '')} sx={{ borderRadius: 'var(--radius-xs)' }} />
                )}
                <Button size="small" onClick={clearAllFilters}
                  sx={{ textTransform: 'none', color: 'var(--color-muted)', minWidth: 'auto' }}>
                  Clear all
                </Button>
              </Box>
            )}
          </Box>

          {/* Filter bar */}
          {showFilters && (
            <Box sx={{
              p: 2,
              borderBottom: '1px solid var(--color-outline)',
              bgcolor: 'var(--color-surface-2)',
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}>
              <FilterSelect label="Origin" value={filterValues.origin}
                options={filterOptions.origins} onChange={(v) => handleFilterChange('origin', v)} />
              <FilterSelect label="Box Number" value={filterValues.boxNumber}
                options={filterOptions.boxNumbers} onChange={(v) => handleFilterChange('boxNumber', v)} />
              <FilterSelect label="Frozen By" value={filterValues.frozenBy}
                options={filterOptions.frozenBy} onChange={(v) => handleFilterChange('frozenBy', v)} />
              <FilterSelect label="Position" value={filterValues.positionInBox}
                options={filterOptions.positionsInBox} onChange={(v) => handleFilterChange('positionInBox', v)} />
              <Button
                variant="text"
                onClick={clearAllFilters}
                startIcon={<i className="fas fa-xmark" />}
                sx={{ textTransform: 'none', color: 'var(--color-muted)', height: 40 }}
              >
                Clear all
              </Button>
            </Box>
          )}

          {/* Table controls: bulk actions (left) + view controls (right) */}
          <Box sx={{
            px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1, flexWrap: 'wrap', borderBottom: '1px solid var(--color-outline)',
            minHeight: 52, bgcolor: selectedIds.length > 0 ? 'var(--color-primary-container)' : 'transparent',
            transition: 'background 0.2s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {selectedIds.length > 0 ? (
                <>
                  <Typography sx={{ fontWeight: 700, color: 'var(--color-on-primary-container)' }}>
                    {selectedIds.length} selected
                  </Typography>
                  {bulkEnabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<i className={userRole === 'user' ? 'fas fa-paper-plane' : 'fas fa-trash'} />}
                      onClick={openBulkDelete}
                      sx={{ textTransform: 'none', borderRadius: 'var(--radius-xs)' }}
                    >
                      {userRole === 'user' ? 'Request Deletion' : 'Delete'}
                    </Button>
                  )}
                  <Button size="small" onClick={() => setSelectedIds([])} sx={{ textTransform: 'none', color: 'var(--color-muted)' }}>
                    Clear
                  </Button>
                </>
              ) : (
                <Typography variant="caption" sx={{ color: 'var(--color-muted)' }}>
                  {totalItems} record{totalItems === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={density === 'compact' ? 'Comfortable rows' : 'Compact rows'}>
                  <IconButton
                    size="small"
                    aria-label="Toggle row density"
                    onClick={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}
                  >
                    <i className={`fas ${density === 'compact' ? 'fa-bars' : 'fa-grip-lines'}`} style={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Columns">
                  <IconButton
                    size="small"
                    aria-label="Choose columns"
                    onClick={(e) => setColMenuAnchor(e.currentTarget)}
                  >
                    <i className="fas fa-table-columns" style={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {isMobile ? (
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <Box key={i} className="skeleton" sx={{ height: 84, borderRadius: 'var(--radius-md)' }} />
                  ))
                : cryopreservations.map((item) => {
                    const id = rowId(item);
                    return (
                      <Card
                        key={id}
                        onClick={() => openDetailModal(item)}
                        sx={{ p: 1.5, cursor: 'pointer', border: '1px solid var(--color-outline)', bgcolor: 'var(--color-surface)', boxShadow: 'none', borderRadius: 'var(--radius-md)' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {bulkEnabled && (
                            <Checkbox
                              size="small"
                              checked={selectedIds.includes(id)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelectOne(id)}
                              inputProps={{ 'aria-label': `Select ${item.cell_line_name}` }}
                            />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700 }} noWrap>{item.cell_line_name || '-'}</Typography>
                            <Typography variant="caption" sx={{ color: 'var(--color-muted)' }}>
                              Box {item.box_number || '-'} · Pos {item.position_in_box || '-'} · {item.number_of_vials_frozen || 0} vials
                            </Typography>
                          </Box>
                          {item.deletion_pending && (
                            <Chip label="Pending" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${item.cell_line_name || 'record'}`}
                            onClick={(e) => openRowMenu(e, item)}
                          >
                            <i className="fas fa-ellipsis-vertical" style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }} />
                          </IconButton>
                        </Box>
                      </Card>
                    );
                  })}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{
              background: 'transparent',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              border: 'none',
              boxShadow: 'none',
              transition: 'none',
              borderRadius: 0,
              overflowX: 'auto'
            }}>
              <Table stickyHeader size={density === 'compact' ? 'small' : 'medium'} sx={{ minWidth: 1080 }}>
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    {bulkEnabled && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someOnPageSelected}
                          checked={allOnPageSelected}
                          onChange={toggleSelectAll}
                          inputProps={{ 'aria-label': 'Select all rows on this page' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, width: 80, textAlign: 'center' }}>
                      Sl. No.
                    </TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{ minWidth: col.minWidth, fontWeight: 700, textAlign: 'center', '& .MuiTableSortLabel-root': { display: 'flex', justifyContent: 'center' } }}
                        sortDirection={orderBy === col.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sx={{ width: 48 }} aria-label="Actions" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 8 }).map((_, r) => (
                      <TableRow key={`sk-${r}`}>
                        {Array.from({ length: (bulkEnabled ? 1 : 0) + visibleColumns.length + 2 }).map((__, c) => (
                          <TableCell key={c} sx={{ textAlign: 'center' }}>
                            <Box
                              className="skeleton"
                              sx={{ height: 16, width: '60%', mx: 'auto', borderRadius: 'var(--radius-xs)' }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : cryopreservations?.length > 0 ? (
                    cryopreservations.map((item, index) => {
                      const id = rowId(item);
                      const cellPy = density === 'compact' ? 0.5 : 1.25;
                      return (
                        <TableRow
                          key={id}
                          hover
                          selected={selectedIds.includes(id)}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openDetailModal(item)}
                        >
                          {bulkEnabled && (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedIds.includes(id)}
                                onChange={() => toggleSelectOne(id)}
                                inputProps={{ 'aria-label': `Select ${item.cell_line_name}` }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ textAlign: 'center', py: cellPy }}>{page * rowsPerPage + index + 1}</TableCell>
                          {visibleColumns.map((col) => (
                            <TableCell key={col.id} sx={{ textAlign: 'center', py: cellPy }}>
                              {col.id === 'cell_line_name' ? (
                                <>
                                  {item.cell_line_name || '-'}
                                  {item.deletion_pending && (
                                    <Chip
                                      label="Pending Deletion"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ ml: 1.5, height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                                    />
                                  )}
                                </>
                              ) : col.render ? (
                                col.render(item)
                              ) : (
                                item[col.id] || '-'
                              )}
                            </TableCell>
                          ))}
                          <TableCell sx={{ px: 0 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              aria-label={`Actions for ${item.cell_line_name || 'record'}`}
                              onClick={(e) => openRowMenu(e, item)}
                            >
                              <i className="fas fa-ellipsis-vertical" style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Column show/hide menu */}
          <Menu anchorEl={colMenuAnchor} open={Boolean(colMenuAnchor)} onClose={() => setColMenuAnchor(null)}>
            {COLUMNS.map((col) => (
              <MenuItem
                key={col.id}
                onClick={() => toggleColumn(col.id)}
                disabled={col.always}
                dense
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={col.always || !hiddenCols.includes(col.id)}
                    disableRipple
                    tabIndex={-1}
                  />
                </ListItemIcon>
                <ListItemText primary={col.label} />
              </MenuItem>
            ))}
          </Menu>

          {/* Per-row action menu */}
          <Menu anchorEl={rowMenuAnchor} open={Boolean(rowMenuAnchor)} onClose={closeRowMenu}>
            <MenuItem onClick={() => { const it = rowMenuItem; closeRowMenu(); openDetailModal(it); }}>
              <ListItemIcon sx={{ minWidth: 32 }}><i className="fas fa-eye" /></ListItemIcon>
              <ListItemText primary="View details" />
            </MenuItem>
            {userRole !== 'viewer' && (
              <MenuItem onClick={() => { const it = rowMenuItem; closeRowMenu(); openEditModal(it); }}>
                <ListItemIcon sx={{ minWidth: 32 }}><i className="fas fa-edit" /></ListItemIcon>
                <ListItemText primary="Edit" />
              </MenuItem>
            )}
            {userRole !== 'viewer' && (
              <MenuItem
                onClick={() => { const it = rowMenuItem; closeRowMenu(); openDetailModal(it); }}
                disabled={rowMenuItem ? !(Number(rowMenuItem.number_of_vials_frozen) > 0) : true}
              >
                <ListItemIcon sx={{ minWidth: 32 }}><i className="fas fa-vial" style={{ color: 'var(--color-sage)' }} /></ListItemIcon>
                <ListItemText primary="Revive vials" />
              </MenuItem>
            )}
            {userRole !== 'viewer' && <Divider />}
            {userRole !== 'viewer' && (
              <MenuItem
                onClick={() => { const it = rowMenuItem; closeRowMenu(); handleDeleteItem(it._id || it.id); }}
                sx={{ color: 'var(--color-accent)' }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <i className={userRole === 'user' ? 'fas fa-paper-plane' : 'fas fa-trash'} style={{ color: 'var(--color-accent)' }} />
                </ListItemIcon>
                <ListItemText primary={userRole === 'user' ? 'Request deletion' : 'Delete'} />
              </MenuItem>
            )}
          </Menu>

          {cryopreservations.length === 0 && !loading && !error && (
            <Box sx={{ py: 7, px: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'var(--color-surface-2)',
                  color: 'var(--color-faint)',
                }}
              >
                <i className="fas fa-vials" style={{ fontSize: '1.6rem' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--color-fg)' }}>
                No cryopreservations found
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--color-muted)', mt: 0.5 }}>
                Adjust your search or filters to see records.
              </Typography>
            </Box>
          )}

          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            sx={{
              borderTop: 1,
              borderColor: 'var(--color-outline)',
              bgcolor: 'transparent',
              color: 'var(--color-muted)',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                color: 'var(--color-muted)',
              },
              '.MuiTablePagination-actions .MuiIconButton-root': {
                color: 'var(--color-muted)',
              },
              '.MuiTablePagination-select': {
                color: 'var(--color-primary)',
              },
            }}
          />
        </CardContent>
      </Card>

      <CryoModal
        open={showAddModal}
        mode="add"
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddItem}
        loading={modalLoading}
        error={error}
        existingItems={cryopreservations}
      />

      <CryoModal
        open={showEditModal}
        mode="edit"
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditItem}
        item={currentItem}
        loading={modalLoading}
        error={error}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        existingItems={cryopreservations}
      />

      <CryoModal
        open={showDetailModal}
        mode="view"
        onClose={() => setShowDetailModal(false)}
        onSubmit={() => {}} // Not used in view mode
        item={currentItem}
        loading={modalLoading}
        error={error}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onRevive={handleReviveItem}
        existingItems={cryopreservations}
        onOpenEdit={openEditModal}
      />

      <CryoModal
        open={showBatchUploadModal}
        mode="batch"
        onClose={() => setShowBatchUploadModal(false)}
        onSubmit={refresh}
        loading={modalLoading}
        error={error}
      />

      <RemainingSpaceModal
        open={showRemainingSpaceModal}
        onClose={() => setShowRemainingSpaceModal(false)}
        remainingSpaceData={remainingSpaceData}
        stats={statsData}
        loading={remainingSpaceLoading}
        error={remainingSpaceError}
        onRefresh={handleShowRemainingSpace}
      />

      {/* Electronic Signature Modal (21 CFR Part 11 Subpart C) */}
      <Dialog 
        open={esignOpen} 
        onClose={() => !modalLoading && (setEsignOpen(false), setBulkDeleteMode(false))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: glassDialogPaperProps.className,
          sx: { ...glassDialogPaperProps.sx, p: 1.5 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <i className="fas fa-signature" style={{ color: approveMode ? 'var(--color-sage)' : 'var(--color-accent)' }}></i>
          <span>{approveMode ? 'Electronic Signature Required: Deletion Approval' : 'Electronic Signature Required'}</span>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2.5, color: 'var(--color-muted)', fontSize: '0.85rem', lineHeight: 1.4 }}>
            In compliance with <strong>21 CFR Part 11 Subpart C</strong>, critical actions (such as deleting or approving deletion of records) must be verified by electronic signature. 
            By entering your password below, you verify your identity as <strong>{user?.email}</strong> and authorize this operation.
          </Typography>
          
          {esignError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 'var(--radius-sm)', fontSize: '0.8rem', py: 0 }}>
              {esignError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Signer Email"
              value={user?.email || ''}
              disabled
              fullWidth
              size="small"
              sx={{ '& .MuiInputBase-input': { fontWeight: 'bold' } }}
            />
            
            <TextField
              label="Signer Password"
              type="password"
              placeholder="Enter your password to sign"
              value={esignPassword}
              onChange={(e) => setEsignPassword(e.target.value)}
              disabled={modalLoading}
              fullWidth
              size="small"
              required
              autoFocus
            />
            
            <TextField
              label={approveMode ? "Reason for Deletion (Entered by Requester)" : "Reason for Deletion"}
              multiline
              rows={2}
              placeholder="Provide a reason for deleting this record"
              value={esignReason}
              onChange={(e) => setEsignReason(e.target.value)}
              disabled={modalLoading || approveMode}
              fullWidth
              size="small"
              required={!approveMode}
              InputProps={{
                readOnly: approveMode,
              }}
            />
            
            <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5, color: 'var(--color-muted)' }}>
              Meaning of Signature: {approveMode ? 'Record Deletion Approval Authorization' : 'Record Deletion Authorization'}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button 
            variant="text"
            color="inherit"
            onClick={() => { setEsignOpen(false); setBulkDeleteMode(false); }}
            disabled={modalLoading}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color={approveMode ? "success" : "error"} 
            onClick={handleConfirmDeleteEsign}
            disabled={modalLoading || !esignPassword || (!approveMode && !esignReason.trim())}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
            startIcon={modalLoading ? <CircularProgress size={16} color="inherit" /> : <i className="fas fa-key" />}
          >
            {modalLoading ? 'Verifying...' : approveMode ? 'Sign & Approve' : 'Sign & Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Deletion Confirmation Dialog */}
      <Dialog
        open={rejectConfirmOpen}
        onClose={() => setRejectConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: glassDialogPaperProps.className,
          sx: { ...glassDialogPaperProps.sx, p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="fas fa-ban" style={{ color: 'var(--color-accent)' }} />
          Reject Deletion Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Are you sure you want to reject the deletion request for{' '}
            <strong>{rejectTargetName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The record will be restored to its normal active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setRejectConfirmOpen(false)}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
            startIcon={<i className="fas fa-ban" />}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Cryopreservation;